/**
 * Utility functions for handling attachment requirements and validation
 */

// Document type constants
export const DOCUMENT_TYPES = {
  BIRTH_CERTIFICATE_FRONT: "Birth_Certificate_Front",
  BIRTH_CERTIFICATE_BACK: "Birth_Certificate_Back",
  DD214_FRONT: "DD214_Front",
  DD214_BACK: "DD214_Back",
  DLPHOTO_FRONT: "DLPhoto_Front",
  DLPHOTO_BACK: "DLPhoto_Back",
  EMPLOYMENT_AUTHORIZATION_CARD_FRONT: "Employment_Authorization_Card_Front",
  EMPLOYMENT_AUTHORIZATION_CARD_BACK: "Employment_Authorization_Card_Back",
  PERMANENT_RESIDENT_CARD_FRONT: "Permanent_Resident_Card_Front",
  PERMANENT_RESIDENT_CARD_BACK: "Permanent_Resident_Card_Back",
  SOCIAL_SECURITY_CARD_FRONT: "Social_Security_Card_Front",
  SOCIAL_SECURITY_CARD_BACK: "Social_Security_Card_Back",
  US_PASSPORT_FRONT: "US_Passport_Front",
  US_PASSPORT_BACK: "US_Passport_Back",
};

/**
 * Determines what empty boxes should be shown based on existing attachments
 * @param {Array} filesFinal - Array of attachment files
 * @returns {Array} Array of empty box objects with type and description
 */
export const getRequiredEmptyBoxes = (filesFinal) => {
  const emptyBoxes = [];

  if (!filesFinal.length) {
    emptyBoxes.push({
      type: "Upload_Front",
      description: "Please upload front of any required document",
    });
    return emptyBoxes;
  }

  const fileTypes = filesFinal.map((file) => file.type);

  // Check if no documents from DOCUMENT_TYPES have been uploaded yet
  const hasAnyDocumentType = Object.values(DOCUMENT_TYPES).some((type) => fileTypes.includes(type));

  if (!hasAnyDocumentType) {
    emptyBoxes.push({
      type: "Upload_Front",
      description: "Please upload front of any required document",
    });
    return emptyBoxes;
  }

  // Helper function to check if a document pair is complete
  const isDocumentPairComplete = (frontType, backType) => {
    return fileTypes.includes(frontType) && fileTypes.includes(backType);
  };

  // Check for complete document sets (both sides required)
  const hasUSPassportComplete = isDocumentPairComplete(
    DOCUMENT_TYPES.US_PASSPORT_FRONT,
    DOCUMENT_TYPES.US_PASSPORT_BACK
  );
  const hasPermanentResidentComplete = isDocumentPairComplete(
    DOCUMENT_TYPES.PERMANENT_RESIDENT_CARD_FRONT,
    DOCUMENT_TYPES.PERMANENT_RESIDENT_CARD_BACK
  );
  const hasEmploymentAuthComplete = isDocumentPairComplete(
    DOCUMENT_TYPES.EMPLOYMENT_AUTHORIZATION_CARD_FRONT,
    DOCUMENT_TYPES.EMPLOYMENT_AUTHORIZATION_CARD_BACK
  );
  const hasDLPhotoComplete = isDocumentPairComplete(
    DOCUMENT_TYPES.DLPHOTO_FRONT,
    DOCUMENT_TYPES.DLPHOTO_BACK
  );
  const hasBirthCertificateComplete = isDocumentPairComplete(
    DOCUMENT_TYPES.BIRTH_CERTIFICATE_FRONT,
    DOCUMENT_TYPES.BIRTH_CERTIFICATE_BACK
  );
  const hasSocialSecurityCardComplete = isDocumentPairComplete(
    DOCUMENT_TYPES.SOCIAL_SECURITY_CARD_FRONT,
    DOCUMENT_TYPES.SOCIAL_SECURITY_CARD_BACK
  );
  const hasDD214Complete = isDocumentPairComplete(
    DOCUMENT_TYPES.DD214_FRONT,
    DOCUMENT_TYPES.DD214_BACK
  );

  // Check for single documents (no front/back required)
  const hasBirthCertificate = hasBirthCertificateComplete;
  const hasSocialSecurityCard = hasSocialSecurityCardComplete;
  const hasDD214 = hasDD214Complete;

  // Count valid documents (complete pairs count as 1, single documents count as 1)
  const validDocuments = [];
  if (hasUSPassportComplete) validDocuments.push("US Passport");
  if (hasPermanentResidentComplete) validDocuments.push("Permanent Resident Card");
  if (hasEmploymentAuthComplete) validDocuments.push("Employment Authorization Card");
  if (hasDLPhotoComplete) validDocuments.push("Driver's License");
  if (hasBirthCertificate) validDocuments.push("Birth Certificate");
  if (hasSocialSecurityCard) validDocuments.push("Social Security Card");
  if (hasDD214) validDocuments.push("DD214");

  // Check if any of the three primary document pairs is complete
  const hasPrimaryDocument = hasUSPassportComplete || hasPermanentResidentComplete || hasEmploymentAuthComplete;

  // If we have at least 2 valid documents, no empty boxes needed
  if (validDocuments.length >= 2) {
    return [];
  }

  // If any of the three primary document pairs is complete, no empty boxes needed
  if (hasPrimaryDocument) {
    return [];
  }

  // If we have exactly 1 valid document, we need at least 1 more
  if (validDocuments.length === 1) {
    // Suggest the missing side of incomplete pairs first
    if (
      fileTypes.includes(DOCUMENT_TYPES.US_PASSPORT_FRONT) &&
      !fileTypes.includes(DOCUMENT_TYPES.US_PASSPORT_BACK)
    ) {
      emptyBoxes.push({
        type: DOCUMENT_TYPES.US_PASSPORT_BACK,
        description: "Please upload US Passport Back to complete the set",
      });
    } else if (
      fileTypes.includes(DOCUMENT_TYPES.US_PASSPORT_BACK) &&
      !fileTypes.includes(DOCUMENT_TYPES.US_PASSPORT_FRONT)
    ) {
      emptyBoxes.push({
        type: DOCUMENT_TYPES.US_PASSPORT_FRONT,
        description: "Please upload US Passport Front to complete the set",
      });
    } else if (
      fileTypes.includes(DOCUMENT_TYPES.PERMANENT_RESIDENT_CARD_FRONT) &&
      !fileTypes.includes(DOCUMENT_TYPES.PERMANENT_RESIDENT_CARD_BACK)
    ) {
      emptyBoxes.push({
        type: DOCUMENT_TYPES.PERMANENT_RESIDENT_CARD_BACK,
        description: "Please upload Permanent Resident Card Back to complete the set",
      });
    } else if (
      fileTypes.includes(DOCUMENT_TYPES.PERMANENT_RESIDENT_CARD_BACK) &&
      !fileTypes.includes(DOCUMENT_TYPES.PERMANENT_RESIDENT_CARD_FRONT)
    ) {
      emptyBoxes.push({
        type: DOCUMENT_TYPES.PERMANENT_RESIDENT_CARD_FRONT,
        description: "Please upload Permanent Resident Card Front to complete the set",
      });
    } else if (
      fileTypes.includes(DOCUMENT_TYPES.EMPLOYMENT_AUTHORIZATION_CARD_FRONT) &&
      !fileTypes.includes(DOCUMENT_TYPES.EMPLOYMENT_AUTHORIZATION_CARD_BACK)
    ) {
      emptyBoxes.push({
        type: DOCUMENT_TYPES.EMPLOYMENT_AUTHORIZATION_CARD_BACK,
        description: "Please upload Employment Authorization Card Back to complete the set",
      });
    } else if (
      fileTypes.includes(DOCUMENT_TYPES.EMPLOYMENT_AUTHORIZATION_CARD_BACK) &&
      !fileTypes.includes(DOCUMENT_TYPES.EMPLOYMENT_AUTHORIZATION_CARD_FRONT)
    ) {
      emptyBoxes.push({
        type: DOCUMENT_TYPES.EMPLOYMENT_AUTHORIZATION_CARD_FRONT,
        description: "Please upload Employment Authorization Card Front to complete the set",
      });
    } else if (
      fileTypes.includes(DOCUMENT_TYPES.DLPHOTO_FRONT) &&
      !fileTypes.includes(DOCUMENT_TYPES.DLPHOTO_BACK)
    ) {
      emptyBoxes.push({
        type: DOCUMENT_TYPES.DLPHOTO_BACK,
        description: "Please upload Driver License Photo Back to complete the set",
      });
    } else if (
      fileTypes.includes(DOCUMENT_TYPES.DLPHOTO_BACK) &&
      !fileTypes.includes(DOCUMENT_TYPES.DLPHOTO_FRONT)
    ) {
      emptyBoxes.push({
        type: DOCUMENT_TYPES.DLPHOTO_FRONT,
        description: "Please upload Driver License Photo Front to complete the set",
      });
    } else if (
      fileTypes.includes(DOCUMENT_TYPES.BIRTH_CERTIFICATE_FRONT) &&
      !fileTypes.includes(DOCUMENT_TYPES.BIRTH_CERTIFICATE_BACK)
    ) {
      emptyBoxes.push({
        type: DOCUMENT_TYPES.BIRTH_CERTIFICATE_BACK,
        description: "Please upload Birth Certificate Back to complete the set",
      });
    } else if (
      fileTypes.includes(DOCUMENT_TYPES.BIRTH_CERTIFICATE_BACK) &&
      !fileTypes.includes(DOCUMENT_TYPES.BIRTH_CERTIFICATE_FRONT)
    ) {
      emptyBoxes.push({
        type: DOCUMENT_TYPES.BIRTH_CERTIFICATE_FRONT,
        description: "Please upload Birth Certificate Front to complete the set",
      });
    } else if (
      fileTypes.includes(DOCUMENT_TYPES.SOCIAL_SECURITY_CARD_FRONT) &&
      !fileTypes.includes(DOCUMENT_TYPES.SOCIAL_SECURITY_CARD_BACK)
    ) {
      emptyBoxes.push({
        type: DOCUMENT_TYPES.SOCIAL_SECURITY_CARD_BACK,
        description: "Please upload Social Security Card Back to complete the set",
      });
    } else if (
      fileTypes.includes(DOCUMENT_TYPES.SOCIAL_SECURITY_CARD_BACK) &&
      !fileTypes.includes(DOCUMENT_TYPES.SOCIAL_SECURITY_CARD_FRONT)
    ) {
      emptyBoxes.push({
        type: DOCUMENT_TYPES.SOCIAL_SECURITY_CARD_FRONT,
        description: "Please upload Social Security Card Front to complete the set",
      });
    } else if (
      fileTypes.includes(DOCUMENT_TYPES.DD214_FRONT) &&
      !fileTypes.includes(DOCUMENT_TYPES.DD214_BACK)
    ) {
      emptyBoxes.push({
        type: DOCUMENT_TYPES.DD214_BACK,
        description: "Please upload DD214 Back to complete the set",
      });
    } else if (
      fileTypes.includes(DOCUMENT_TYPES.DD214_BACK) &&
      !fileTypes.includes(DOCUMENT_TYPES.DD214_FRONT)
    ) {
      emptyBoxes.push({
        type: DOCUMENT_TYPES.DD214_FRONT,
        description: "Please upload DD214 Front to complete the set",
      });
    } else {
      // Suggest additional documents
      if (!hasBirthCertificate) {
        emptyBoxes.push({
          type: "Birth_Certificate_Front_And_Back",
          description: "Please upload Birth Certificate Front and Back",
        });
      }
      if (!hasSocialSecurityCard) {
        emptyBoxes.push({
          type: "Social_Security_Card_Front_And_Back",
          description: "Please upload Social Security Card Front and Back",
        });
      }
      if (!hasDD214) {
        emptyBoxes.push({
          type: "DD214_Front_And_Back",
          description: "Please upload DD214 Front and Back",
        });
      }
    }
    return emptyBoxes;
  }

  // If we have 0 valid documents, suggest completing existing incomplete pairs first
  const incompletePairs = [];

  // Check for incomplete US Passport
  if (
    fileTypes.includes(DOCUMENT_TYPES.US_PASSPORT_FRONT) ||
    fileTypes.includes(DOCUMENT_TYPES.US_PASSPORT_BACK)
  ) {
    if (!fileTypes.includes(DOCUMENT_TYPES.US_PASSPORT_BACK)) {
      incompletePairs.push({
        type: DOCUMENT_TYPES.US_PASSPORT_BACK,
        description: "Please upload US Passport Back",
      });
    }
    if (!fileTypes.includes(DOCUMENT_TYPES.US_PASSPORT_FRONT)) {
      incompletePairs.push({
        type: DOCUMENT_TYPES.US_PASSPORT_FRONT,
        description: "Please upload US Passport Front",
      });
    }
  }

  // Check for incomplete Permanent Resident Card
  if (
    fileTypes.includes(DOCUMENT_TYPES.PERMANENT_RESIDENT_CARD_FRONT) ||
    fileTypes.includes(DOCUMENT_TYPES.PERMANENT_RESIDENT_CARD_BACK)
  ) {
    if (!fileTypes.includes(DOCUMENT_TYPES.PERMANENT_RESIDENT_CARD_BACK)) {
      incompletePairs.push({
        type: DOCUMENT_TYPES.PERMANENT_RESIDENT_CARD_BACK,
        description: "Please upload Permanent Resident Card Back",
      });
    }
    if (!fileTypes.includes(DOCUMENT_TYPES.PERMANENT_RESIDENT_CARD_FRONT)) {
      incompletePairs.push({
        type: DOCUMENT_TYPES.PERMANENT_RESIDENT_CARD_FRONT,
        description: "Please upload Permanent Resident Card Front",
      });
    }
  }

  // Check for incomplete Employment Authorization Card
  if (
    fileTypes.includes(DOCUMENT_TYPES.EMPLOYMENT_AUTHORIZATION_CARD_FRONT) ||
    fileTypes.includes(DOCUMENT_TYPES.EMPLOYMENT_AUTHORIZATION_CARD_BACK)
  ) {
    if (!fileTypes.includes(DOCUMENT_TYPES.EMPLOYMENT_AUTHORIZATION_CARD_BACK)) {
      incompletePairs.push({
        type: DOCUMENT_TYPES.EMPLOYMENT_AUTHORIZATION_CARD_BACK,
        description: "Please upload Employment Authorization Card Back",
      });
    }
    if (!fileTypes.includes(DOCUMENT_TYPES.EMPLOYMENT_AUTHORIZATION_CARD_FRONT)) {
      incompletePairs.push({
        type: DOCUMENT_TYPES.EMPLOYMENT_AUTHORIZATION_CARD_FRONT,
        description: "Please upload Employment Authorization Card Front",
      });
    }
  }

  // Check for incomplete Driver's License
  if (
    fileTypes.includes(DOCUMENT_TYPES.DLPHOTO_FRONT) ||
    fileTypes.includes(DOCUMENT_TYPES.DLPHOTO_BACK)
  ) {
    if (!fileTypes.includes(DOCUMENT_TYPES.DLPHOTO_BACK)) {
      incompletePairs.push({
        type: DOCUMENT_TYPES.DLPHOTO_BACK,
        description: "Please upload Driver License Photo Back",
      });
    }
    if (!fileTypes.includes(DOCUMENT_TYPES.DLPHOTO_FRONT)) {
      incompletePairs.push({
        type: DOCUMENT_TYPES.DLPHOTO_FRONT,
        description: "Please upload Driver License Photo Front",
      });
    }
  }

  // Check for incomplete Birth Certificate
  if (
    fileTypes.includes(DOCUMENT_TYPES.BIRTH_CERTIFICATE_FRONT) ||
    fileTypes.includes(DOCUMENT_TYPES.BIRTH_CERTIFICATE_BACK)
  ) {
    if (!fileTypes.includes(DOCUMENT_TYPES.BIRTH_CERTIFICATE_BACK)) {
      incompletePairs.push({
        type: DOCUMENT_TYPES.BIRTH_CERTIFICATE_BACK,
        description: "Please upload Birth Certificate Back",
      });
    }
    if (!fileTypes.includes(DOCUMENT_TYPES.BIRTH_CERTIFICATE_FRONT)) {
      incompletePairs.push({
        type: DOCUMENT_TYPES.BIRTH_CERTIFICATE_FRONT,
        description: "Please upload Birth Certificate Front",
      });
    }
  }

  // Check for incomplete Social Security Card
  if (
    fileTypes.includes(DOCUMENT_TYPES.SOCIAL_SECURITY_CARD_FRONT) ||
    fileTypes.includes(DOCUMENT_TYPES.SOCIAL_SECURITY_CARD_BACK)
  ) {
    if (!fileTypes.includes(DOCUMENT_TYPES.SOCIAL_SECURITY_CARD_BACK)) {
      incompletePairs.push({
        type: DOCUMENT_TYPES.SOCIAL_SECURITY_CARD_BACK,
        description: "Please upload Social Security Card Back",
      });
    }
    if (!fileTypes.includes(DOCUMENT_TYPES.SOCIAL_SECURITY_CARD_FRONT)) {
      incompletePairs.push({
        type: DOCUMENT_TYPES.SOCIAL_SECURITY_CARD_FRONT,
        description: "Please upload Social Security Card Front",
      });
    }
  }

  // Check for incomplete DD214
  if (
    fileTypes.includes(DOCUMENT_TYPES.DD214_FRONT) ||
    fileTypes.includes(DOCUMENT_TYPES.DD214_BACK)
  ) {
    if (!fileTypes.includes(DOCUMENT_TYPES.DD214_BACK)) {
      incompletePairs.push({
        type: DOCUMENT_TYPES.DD214_BACK,
        description: "Please upload DD214 Back",
      });
    }
    if (!fileTypes.includes(DOCUMENT_TYPES.DD214_FRONT)) {
      incompletePairs.push({
        type: DOCUMENT_TYPES.DD214_FRONT,
        description: "Please upload DD214 Front",
      });
    }
  }

  // Add incomplete pairs to empty boxes
  emptyBoxes.push(...incompletePairs);

  // If no incomplete pairs, suggest new documents
  if (incompletePairs.length === 0) {
    if (!hasBirthCertificate) {
      emptyBoxes.push({
        type: "Birth_Certificate_Front_And_Back",
        description: "Please upload Birth Certificate Front and Back",
      });
    }
    if (!hasSocialSecurityCard) {
      emptyBoxes.push({
        type: "Social_Security_Card_Front_And_Back",
        description: "Please upload Social Security Card Front and Back",
      });
    }
    if (!hasDD214) {
      emptyBoxes.push({
        type: "DD214_Front_And_Back",
        description: "Please upload DD214 Front and Back",
      });
    }
  }

  return emptyBoxes;
};
